/* FERE framed film carousel. Native videos load only when first selected. */
(() => {
  'use strict';
  const initialize = (scope = document) => {
    const heroes = [...(scope.matches?.('[data-fere-film-hero]') ? [scope] : []), ...scope.querySelectorAll('[data-fere-film-hero]')];
    heroes.forEach((hero) => {
      if (hero.dataset.filmReady) return;
      hero.dataset.filmReady = 'true';
      const frame = hero.querySelector('[data-film-frame]');
      const slides = [...hero.querySelectorAll('[data-film-slide]')];
      const dots = [...hero.querySelectorAll('[data-film-dot]')];
      const toggle = hero.querySelector('[data-film-playback]');
      const status = hero.querySelector('[data-film-status]');
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const controller = new AbortController();
      const { signal } = controller;
      const duration = 6750;
      let index = 0;
      let paused = motion.matches || hero.dataset.autoplay !== 'true';
      let inView = true;
      let hovered = false;
      let focused = false;
      let explicitPlay = false;
      let elapsed = 0;
      let lastTick = 0;
      let raf = 0;
      let playAttempt = 0;
      let touchStart = null;
      let destroyed = false;
      let focusTimer = null;
      const activeVideo = () => slides[index]?.querySelector('[data-film-video]');
      const canPlay = () => !destroyed && !paused && !document.hidden && inView;
      const canRotate = () => canPlay() && !hovered && (!focused || explicitPlay);
      const updateControl = () => {
        hero.dataset.filmPaused = String(paused);
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      };
      const stopAll = () => {
        playAttempt += 1;
        slides.forEach((slide) => slide.querySelector('video')?.pause());
      };
      const stopTick = () => { cancelAnimationFrame(raf); raf = 0; lastTick = 0; };
      const updateProgress = () => hero.style.setProperty('--film-progress', String(Math.min(1, elapsed / duration)));
      const startVideo = () => {
        const video = activeVideo();
        if (!video || !canPlay() || video.dataset.failed === 'true') return;
        if (video.dataset.loaded !== 'true') {
          video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
          video.dataset.loaded = 'true';
          video.muted = true;
          video.defaultMuted = true;
          video.load();
        }
        const attempt = ++playAttempt;
        const promise = video.play();
        if (promise?.catch) promise.catch(() => {
          if (attempt !== playAttempt || video !== activeVideo() || !canPlay()) return;
          slides[index].classList.remove('has-video-frame');
          paused = true;
          stopTick();
          stopAll();
          updateControl();
          status.textContent = 'Film playback is paused. Select Play slideshow to try again.';
        });
      };
      const tick = (time) => {
        raf = 0;
        if (!hero.isConnected) { destroy(); return; }
        if (canRotate() && slides.length > 1) {
          if (lastTick) elapsed += Math.min(time - lastTick, 250);
          if (elapsed >= duration) { show(index + 1); return; }
          updateProgress();
        }
        lastTick = time;
        if (canPlay()) raf = requestAnimationFrame(tick);
      };
      const sync = () => {
        if (destroyed) return;
        stopTick();
        if (canPlay()) {
          startVideo();
          raf = requestAnimationFrame(tick);
        } else stopAll();
        updateControl();
      };
      const show = (next, manual = false) => {
        if (destroyed) return;
        stopAll();
        index = (next + slides.length) % slides.length;
        elapsed = 0;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
          slide.setAttribute('aria-hidden', String(i !== index));
          if (i === index) {
            const video = slide.querySelector('video');
            if (video?.dataset.loaded === 'true') {
              try { video.currentTime = 0; } catch (_) { /* Wait for media metadata. */ }
            }
          }
        });
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        hero.dataset.filmActive = String(index + 1);
        if (manual) status.textContent = `Film ${index + 1} of ${slides.length}`;
        updateProgress();
        sync();
      };
      slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;
        video.addEventListener('playing', () => {
          if (video !== activeVideo() || !canPlay()) { video.pause(); return; }
          slide.classList.add('has-video-frame');
        }, { signal });
        video.addEventListener('error', () => {
          video.dataset.failed = 'true';
          slide.classList.remove('has-video-frame');
          video.pause();
          if (video === activeVideo()) status.textContent = 'The film is unavailable. Its still photograph is shown.';
        }, { signal });
        video.addEventListener('ended', () => {
          if (video !== activeVideo() || !canPlay()) return;
          if (slides.length === 1) { video.currentTime = 0; startVideo(); }
          else if (canRotate()) show(index + 1);
        }, { signal });
      });
      toggle.hidden = !slides.some((slide) => slide.querySelector('video')) && slides.length < 2;
      toggle.addEventListener('click', () => {
        paused = !paused;
        explicitPlay = !paused;
        if (!paused && activeVideo()?.dataset.failed === 'true') {
          delete activeVideo().dataset.failed;
          delete activeVideo().dataset.loaded;
        }
        status.textContent = paused ? 'Slideshow paused.' : 'Slideshow playing.';
        sync();
      }, { signal });
      hero.querySelectorAll('[data-film-step]').forEach((button) => {
        button.addEventListener('click', () => show(index + Number(button.dataset.filmStep), true), { signal });
      });
      dots.forEach((button) => button.addEventListener('click', () => show(Number(button.dataset.filmDot), true), { signal }));
      frame.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') show(0, true);
        else if (event.key === 'End') show(slides.length - 1, true);
        else show(index + (event.key === 'ArrowRight' ? 1 : -1), true);
      }, { signal });
      frame.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' && !touchStart) touchStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, { passive: true, signal });
      frame.addEventListener('pointerup', (event) => {
        if (!touchStart || event.pointerId !== touchStart.id) return;
        const dx = event.clientX - touchStart.x;
        const dy = event.clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) show(index + (dx < 0 ? 1 : -1), true);
      }, { passive: true, signal });
      frame.addEventListener('pointercancel', (event) => { if (touchStart?.id === event.pointerId) touchStart = null; }, { passive: true, signal });
      frame.addEventListener('pointerenter', (event) => { if (event.pointerType !== 'touch') hovered = true; }, { signal });
      frame.addEventListener('pointerleave', () => { hovered = false; }, { signal });
      frame.addEventListener('focusin', () => { focused = true; explicitPlay = false; sync(); }, { signal });
      frame.addEventListener('focusout', () => {
        clearTimeout(focusTimer);
        focusTimer = setTimeout(() => {
          if (destroyed) return;
          focused = frame.contains(document.activeElement);
          if (!focused) explicitPlay = false;
          sync();
        }, 0);
      }, { signal });
      document.addEventListener('visibilitychange', sync, { signal });
      motion.addEventListener('change', () => {
        if (motion.matches) { paused = true; explicitPlay = false; }
        sync();
      }, { signal });
      window.addEventListener('pagehide', () => { stopTick(); stopAll(); }, { signal });
      window.addEventListener('pageshow', sync, { signal });
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        inView = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.05 }) : null;
      observer?.observe(frame);
      function destroy() {
        if (destroyed) return;
        destroyed = true;
        clearTimeout(focusTimer);
        stopTick(); stopAll(); observer?.disconnect(); controller.abort(); delete hero.dataset.filmReady;
      }
      hero.closest('.shopify-section')?.addEventListener('shopify:section:unload', destroy, { signal });
      show(0);
    });
  };
  initialize();
  if (!window.fereFilmEditorListener) {
    window.fereFilmEditorListener = true;
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  }
})();
